<template>
    <Dialog v-model:open="open">
        <DialogContent class="sm:max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
            <DialogHeader>
                <DialogTitle>{{ $t('manage.profileImage.title') }}</DialogTitle>
            </DialogHeader>

            <!-- 탭 헤더 -->
            <div class="border-b border-gray-200 dark:border-gray-700">
                <nav class="-mb-px flex gap-6" role="tablist">
                    <button
                        v-for="tab in tabs"
                        :key="tab.key"
                        type="button"
                        role="tab"
                        :aria-selected="activeTab === tab.key"
                        :class="[
                            'whitespace-nowrap border-b-2 py-2 px-1 text-sm font-medium transition-colors',
                            activeTab === tab.key
                                ? 'border-primary text-primary'
                                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                        ]"
                        @click="activeTab = tab.key"
                    >
                        {{ $t(tab.labelKey) }}
                    </button>
                </nav>
            </div>

            <!-- 탭 콘텐츠 영역 — 고정 높이로 탭 전환 시 모달 크기 변하지 않음 -->
            <div class="min-h-[420px] overflow-hidden">
                <!-- 탭 1: 파일 업로드 -->
                <div v-show="activeTab === 'upload' && !sourceImage" class="space-y-4">
                    <label class="flex flex-col items-center justify-center w-full h-[400px] border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800">
                        <Upload class="h-10 w-10 text-gray-400 mb-2" />
                        <span class="text-sm text-gray-500 dark:text-gray-400">{{ $t('manage.profileImage.selectFile') }}</span>
                        <span class="text-xs text-gray-400 mt-1">JPEG / PNG / WebP, 최대 5MB</span>
                        <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            class="hidden"
                            @change="onFileSelected"
                        />
                    </label>
                </div>

                <!-- 탭 2: 이미지 검색 -->
                <div v-show="activeTab === 'search' && !sourceImage" class="space-y-3">
                    <div class="flex gap-2">
                        <Input
                            v-model="searchQuery"
                            :placeholder="$t('manage.profileImage.searchPlaceholder')"
                            class="flex-1"
                            @keyup.enter="runSearch"
                        />
                        <Button :disabled="searching || !searchQuery.trim()" @click="runSearch">
                            <Spinner v-if="searching" class="h-4 w-4" />
                            <Search v-else class="h-4 w-4" />
                        </Button>
                    </div>
                    <p v-if="searchMessage" class="text-xs text-gray-500 dark:text-gray-400">
                        {{ searchMessage }}
                    </p>
                    <div class="grid grid-cols-3 sm:grid-cols-4 gap-3 h-[336px] overflow-y-auto">
                        <div v-for="(item, idx) in searchResults" :key="idx" class="flex flex-col gap-1">
                            <button
                                type="button"
                                class="aspect-square rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 hover:ring-2 hover:ring-primary focus:ring-2 focus:ring-primary focus:outline-none disabled:opacity-50"
                                :disabled="loadingFromUrl"
                                :title="item.author"
                                @click="selectFromSearch(item)"
                            >
                                <img :src="item.thumbnailUrl" class="w-full h-full object-cover" :alt="item.author" loading="lazy" />
                            </button>
                            <!-- Attribution: 사진사 + Provider 양쪽 링크 (UTM 백엔드에서 부여) -->
                            <p class="text-[10px] leading-tight text-gray-500 dark:text-gray-400 truncate">
                                Photo by
                                <a v-if="item.authorUrl" :href="item.authorUrl" target="_blank" rel="noopener noreferrer" class="hover:text-primary underline" @click.stop>{{ item.author }}</a>
                                <span v-else>{{ item.author }}</span>
                                <template v-if="item.provider === 'unsplash'">
                                    on <a href="https://unsplash.com/?utm_source=wmdpass&utm_medium=referral" target="_blank" rel="noopener noreferrer" class="hover:text-primary underline" @click.stop>Unsplash</a>
                                </template>
                                <template v-else-if="item.provider === 'pexels'">
                                    on <a href="https://www.pexels.com/" target="_blank" rel="noopener noreferrer" class="hover:text-primary underline" @click.stop>Pexels</a>
                                </template>
                                <template v-else-if="item.provider === 'pixabay'">
                                    on <a href="https://pixabay.com/" target="_blank" rel="noopener noreferrer" class="hover:text-primary underline" @click.stop>Pixabay</a>
                                </template>
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Cropper (소스 이미지 있을 때만 노출, 양 탭 공통) -->
                <div v-if="sourceImage" class="space-y-3">
                    <!-- 뒤로 가기: 다시 선택 -->
                    <div class="flex items-center justify-between">
                        <Button variant="outline" size="sm" @click="clearSource">
                            <ArrowLeft class="h-4 w-4 mr-1" />
                            {{ $t('manage.profileImage.backToList') }}
                        </Button>
                        <span class="text-xs text-gray-500 dark:text-gray-400">{{ $t('manage.profileImage.cropHint') }}</span>
                    </div>
                    <Cropper
                        ref="cropperRef"
                        :src="sourceImage"
                        :stencil-props="{ aspectRatio: 1 }"
                        :default-size="defaultSize"
                        :default-position="defaultPosition"
                        class="cropper"
                    />
                </div>
            </div>

            <!-- 안내문구 — 안면인식 사진과 분리 (하단 위치) -->
            <div class="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
                {{ $t('manage.profileImage.noticeFaceSeparate') }}
            </div>

            <DialogFooter class="gap-2">
                <Button variant="outline" @click="close" :disabled="saving">
                    {{ $t('common.cancel') }}
                </Button>
                <Button :disabled="!sourceImage || saving" @click="save">
                    <Spinner v-if="saving" class="h-4 w-4 mr-1" />
                    {{ $t('common.save') }}
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { toast } from 'vue-sonner';
import { Cropper } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';
import api from '@/api/index';
import { useAuthStore } from '@stores/auth';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Upload, Search, ArrowLeft } from 'lucide-vue-next';

const props = defineProps({
    modelValue: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const authStore = useAuthStore();

const open = ref(props.modelValue);
watch(() => props.modelValue, (v) => { open.value = v; });
watch(open, (v) => { emit('update:modelValue', v); if (!v) reset(); });

const tabs = [
    { key: 'upload', labelKey: 'manage.profileImage.tabUpload' },
    { key: 'search', labelKey: 'manage.profileImage.tabSearch' },
];
const activeTab = ref('upload');

const sourceImage = ref(null);  // data URL or blob URL for cropper input
const cropperRef = ref(null);
const saving = ref(false);

// 검색 탭 상태
const searchQuery = ref('');
const searchResults = ref([]);
const searchProvider = ref('');
const searchMessage = ref('');
const searching = ref(false);
const loadingFromUrl = ref(false);

function close() {
    open.value = false;
}

function reset() {
    sourceImage.value = null;
    saving.value = false;
    searchResults.value = [];
    searchProvider.value = '';
    searchMessage.value = '';
    searchQuery.value = '';
    activeTab.value = 'upload';
}

function clearSource() {
    sourceImage.value = null;
}

async function onFileSelected(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
        toast.error(t('manage.profileImage.invalidType') || '지원하지 않는 이미지 형식입니다.');
        return;
    }
    if (file.size > 5 * 1024 * 1024) {
        toast.error(t('manage.profileImage.tooLarge') || '이미지가 5MB 를 초과합니다.');
        return;
    }
    sourceImage.value = await readAsDataUrl(file);
    e.target.value = '';
}

function readAsDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

async function runSearch() {
    if (!searchQuery.value.trim()) return;
    try {
        searching.value = true;
        searchMessage.value = '';
        const res = await api.get('/api/manage/image-search', {
            params: { q: searchQuery.value.trim(), perPage: 12 },
        });
        const data = res.data?.data || { items: [], provider: 'none' };
        searchResults.value = data.items || [];
        searchProvider.value = data.provider || '';
        if (searchResults.value.length === 0) {
            searchMessage.value = res.data?.message || t('manage.profileImage.noSearchResult') || '검색 결과가 없습니다.';
        }
    } catch (err) {
        toast.error(err.detail || err.message || t('error.generalError'));
    } finally {
        searching.value = false;
    }
}

async function selectFromSearch(item) {
    try {
        loadingFromUrl.value = true;
        // Unsplash 가이드라인: 사진 '선택' 시점에 download trigger 호출 (백엔드 proxy)
        if (item.downloadLocation) {
            api.post('/api/manage/image-download-trigger', { downloadLocation: item.downloadLocation })
                .catch(() => {}); // 트리거 실패는 무시 (사용자 흐름 막지 않음)
        }
        // 이미지 본체는 백엔드 proxy 로 fetch (CORS 우회)
        const res = await api.get('/api/manage/image-fetch', {
            params: { url: item.fullUrl },
            responseType: 'blob',
        });
        const blob = res.data;
        sourceImage.value = await readAsDataUrl(blob);
    } catch (err) {
        toast.error(t('manage.profileImage.loadFailed') || '이미지 불러오기 실패');
    } finally {
        loadingFromUrl.value = false;
    }
}

// 초기 stencil 크기 — visibleArea 의 짧은 변 80%
function defaultSize({ imageSize, visibleArea }) {
    const area = visibleArea || imageSize;
    const side = Math.min(area.width, area.height) * 0.8;
    return { width: side, height: side };
}

// 초기 stencil 위치 — 중앙
function defaultPosition({ imageSize, visibleArea, coordinates }) {
    const area = visibleArea || imageSize;
    return {
        left: (area.left || 0) + (area.width - coordinates.width) / 2,
        top: (area.top || 0) + (area.height - coordinates.height) / 2,
    };
}

function getCroppedDataUrl() {
    if (!cropperRef.value) return null;
    const result = cropperRef.value.getResult();
    if (!result?.canvas) return null;
    // 256x256 정사각형 리사이즈
    const target = document.createElement('canvas');
    target.width = 256;
    target.height = 256;
    const ctx = target.getContext('2d');
    ctx.drawImage(result.canvas, 0, 0, 256, 256);
    return target.toDataURL('image/jpeg', 0.85);
}

async function save() {
    const dataUrl = getCroppedDataUrl();
    if (!dataUrl) {
        toast.error(t('manage.profileImage.cropFailed') || '크롭 결과를 생성하지 못했습니다.');
        return;
    }
    try {
        saving.value = true;
        await api.post('/api/manage/profile-image', { picture: dataUrl });
        toast.success(t('manage.profileImage.saved') || '프로필 이미지가 변경되었습니다.');
        await authStore.loadProfileImage(); // 헤더 아바타 즉시 갱신
        close();
    } catch (err) {
        toast.error(err.detail || err.message || t('error.generalError'));
    } finally {
        saving.value = false;
    }
}
</script>

<style scoped>
.cropper {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    height: 360px;
    background: #1a1a1a;
    border-radius: 0.5rem;
    overflow: hidden;
    box-sizing: border-box;
}
/* vue-advanced-cropper 내부 wrapper 가 부모 폭을 넘지 않도록 강제 */
.cropper :deep(.vue-advanced-cropper) {
    width: 100%;
    height: 100%;
}
.cropper :deep(.vue-advanced-cropper__wrapper),
.cropper :deep(.vue-advanced-cropper__boundaries),
.cropper :deep(.vue-advanced-cropper__background-wrapper),
.cropper :deep(.vue-advanced-cropper__foreground) {
    max-width: 100%;
    max-height: 100%;
}
</style>
